import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { logout } from 'redux/authSlice';
import type { TRootState } from 'redux/store';
import { ICasesCountResponse, ISignedURLRequest, IUpdateUserProfileRequest, IUploadFileRequest } from './apiTypes';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://api-dev.32co.com';
// const BASE_URL = 'http://localhost:8000/admin'
export const VERSION = process.env.REACT_APP_BASE_URL_VERSION || 'v1';

const baseQuery = fetchBaseQuery({
  baseUrl: BASE_URL,
  prepareHeaders: (headers: any, { getState }) => {
    // By default, if we have a token in the store, let's use that for authenticated requests
    const { token } = (getState() as TRootState).auth;
    if (token) {
      headers.set('Authorization', token);
    }
    // Add default headers
    headers.set('Content-Type', 'application/json');
    headers.set('Accept', '*/*');

    return headers;
  },
});
const baseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const result = await baseQuery(args, api, extraOptions);
  if (result.error && result.error.status === 401) {
    api.dispatch(logout());
    // try to get a new token
    // const refreshResult = await baseQuery('/refreshToken', api, extraOptions)
    // if (refreshResult.data) {
    //   // store the new token
    //   api.dispatch(tokenReceived(refreshResult.data))
    //   // retry the initial query
    //   result = await baseQuery(args, api, extraOptions)
    // }
    // else {
    //   api.dispatch(loggedOut())
    // }
  }
  return result;
};

// initialize an empty api service that we'll inject endpoints into later as needed
export const api = createApi({
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'CasesView',
    'Cases',
    'Users',
    'Practices',
    'Bills',
    'Payments',
    'Discounts',
    'SKUS',
    'Modifications',
    'Proposals',
    'Releases',
  ],
  endpoints: (builder) => ({
    getSignedUrl: builder.mutation<{ url: string; uploadedAt?: string }, ISignedURLRequest>({
      query: (credentials) => ({
        url: `/admin/${VERSION}/signed-url`,
        method: 'POST',
        body: credentials,
      }),
    }),
    uploadFile: builder.mutation<void, IUploadFileRequest>({
      query: (credentials) => ({
        url: credentials.url,
        method: 'PUT',
        body: credentials.file,
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    }),
    getUserProfile: builder.query<any, any>({
      query: ({ id }) => `/admin/${VERSION}/profile/${id}`,
    }),
    updateUserProfile: builder.mutation<void, { id: string; body: IUpdateUserProfileRequest }>({
      query: ({ body, id }) => ({
        url: `/admin/${VERSION}/profile/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    getDashboardCasesCount: builder.query<ICasesCountResponse, void>({
      query: () => `/admin/${VERSION}/analytics/case`,
    }),
  }),
});

export const {
  useGetSignedUrlMutation,
  useUploadFileMutation,
  useGetUserProfileQuery,
  useUpdateUserProfileMutation,
  useGetDashboardCasesCountQuery,
} = api;
